// ABOUTME: Server-paged design-system table of bulk change rows, one row per value, shared by preview and result.
// ABOUTME: Renders node code (opens the node), action, field, language and the change, or the descendants of a delete; steps add their own columns.

import { Box, LinkIcon, Stack, Typography } from '@ds/coolshop';
import { BaseTable } from '@ds/data-table';
import { redirect } from '@src/_new/utils/redirect';
import { SettingsContext } from '@src/modules/setting/SettingsContext';
import { getTablePaginationRowsLabel } from '@src/utils/form/shared';
import { CellContext, ColumnDef } from '@tanstack/react-table';
import { createContext, MouseEvent, useContext, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { useNodesById } from '../hooks/useBulkChangeNodes';
import { BulkChangeAction, FindReplaceRowDto, isDeleteRun } from '../types';
import { actionKindMessages } from '../utils/actionMessages';
import { findRegexOrNull } from '../utils/buildFindPattern';
import { ContentTypeLabeler } from '../utils/contentTypeLabels';
import { linkIdOf, storedValueText } from '../utils/valueDisplay';
import { CascadeDeletePreviewDialog } from './CascadeDeletePreviewDialog';
import { DiffCell, LinkChangeCell, ValueChangeCell } from './DiffCell';
import { nodeOptionLabel } from './NodePicker';

export type RowColumn = ColumnDef<FindReplaceRowDto, unknown> & { maxWidth?: boolean };

type RowCellProps = CellContext<FindReplaceRowDto, unknown>;

const NO_COLUMNS: RowColumn[] = [];

/** What the module-level cell renderers need from the table that renders them. */
type RowsTableContextValue = {
    hierarchyId: string;
    actions: BulkChangeAction[];
    /** The compiled pattern of each replaceText action, by action index. */
    regexByAction: Map<number, RegExp | null>;
    labeler: ContentTypeLabeler;
    nodeLabel: (id: string) => string;
    /** Opens the list of what the cascade delete of a row's node removes. */
    showCascade: (row: FindReplaceRowDto) => void;
};

const LINK_TYPES = ['link', 'media'];

const RowsTableContext = createContext<RowsTableContextValue | null>(null);

const useRowsTableContext = (): RowsTableContextValue => {
    const value = useContext(RowsTableContext);
    if (!value) throw new Error('Find & replace row cells must render inside FindReplaceRowsTable');
    return value;
};

function NodeCodeCell({ row }: RowCellProps) {
    const { hierarchyId } = useRowsTableContext();
    // Plain text in the table, as the design shows it, that still opens the node.
    return (
        <Box
            component="button"
            data-testid={`find-replace-row-${row.original.seq}`}
            onClick={(event: MouseEvent) => {
                event.stopPropagation();
                redirect({ hierarchy: hierarchyId, node: row.original.nodeId });
            }}
            border={0}
            p={0}
            bgcolor="transparent"
            cursor="pointer"
            typography="body2"
            fontFamily="inherit"
            color="text.primary"
            textAlign="left"
        >
            {row.original.nodeCode}
        </Box>
    );
}

/** The node code of a cascade delete row, with the linking objects its cascade also deletes. */
function DeleteNodeCodeCell({ row, ...cell }: RowCellProps) {
    const { formatMessage } = useIntl();
    const { showCascade } = useRowsTableContext();
    const { linkedDeleteCount } = row.original;
    if (!linkedDeleteCount) return <NodeCodeCell row={row} {...cell} />;
    const label = formatMessage(
        {
            id: 'bulkChange.table.linkedDeleteCount',
            defaultMessage: '+{count, plural, one {# linked object} other {# linked objects}}',
        },
        { count: linkedDeleteCount },
    );
    return (
        <Stack direction="row" gap={1} alignItems="center">
            <NodeCodeCell row={row} {...cell} />
            <Box
                component="button"
                data-testid={`bulk-change-cascade-${row.original.seq}`}
                onClick={(event: MouseEvent) => {
                    event.stopPropagation();
                    showCascade(row.original);
                }}
                aria-label={label}
                title={label}
                display="flex"
                alignItems="center"
                gap={0.5}
                border={0}
                p={0}
                bgcolor="transparent"
                cursor="pointer"
                typography="caption"
                fontFamily="inherit"
                color="error.main"
            >
                <LinkIcon fontSize="small" />
                {label}
            </Box>
        </Stack>
    );
}

function DescendantsCell({ row }: RowCellProps) {
    return <span>{row.original.matchCount > 0 ? row.original.matchCount : '—'}</span>;
}

function ActionCell({ row }: RowCellProps) {
    const { formatMessage } = useIntl();
    const { actions } = useRowsTableContext();
    const action = actions[row.original.actionIndex];
    return <span>{action ? formatMessage(actionKindMessages[action.kind]) : ''}</span>;
}

function ContentTypeCell({ row }: RowCellProps) {
    const { labeler } = useRowsTableContext();
    const { ctg, ct, ctgIndex } = row.original;
    const group = ctgIndex > 0 ? ` (${labeler.groupLabel(ctg)} #${ctgIndex})` : '';
    return <span>{`${labeler.typeLabel(ctg, ct)}${group}`}</span>;
}

function LangCell({ row }: RowCellProps) {
    const { labeler } = useRowsTableContext();
    const { ctg, ct, lang } = row.original;
    // A non-localised field has one value for every language.
    const localised = labeler.contentType(ctg, ct)?.multiLanguage ?? true;
    return <span>{localised ? labeler.languageLabel(lang) : '—'}</span>;
}

function ValueDiffCell({ row }: RowCellProps) {
    const { formatMessage } = useIntl();
    const { actions, regexByAction, labeler, nodeLabel } = useRowsTableContext();
    const { actionIndex, ctg, ct, before, after, beforeValue, afterValue } = row.original;
    const action = actions[actionIndex];
    if (action?.kind === 'replaceText') {
        return (
            <DiffCell
                before={before}
                after={after}
                regex={regexByAction.get(actionIndex) ?? null}
                replace={action.replace}
            />
        );
    }
    const type = labeler.contentType(ctg, ct)?.type ?? '';
    if (LINK_TYPES.includes(type)) {
        return (
            <LinkChangeCell
                beforeValue={beforeValue}
                afterValue={afterValue}
                nodeLabel={nodeLabel}
            />
        );
    }
    const valueText = (item: unknown) => {
        const text = storedValueText(item);
        if (type === 'select') return labeler.optionLabel(ctg, ct, text);
        if (type === 'boolean') {
            return text === 'true'
                ? formatMessage({ id: 'bulkChange.boolean.true', defaultMessage: 'Yes' })
                : formatMessage({ id: 'bulkChange.boolean.false', defaultMessage: 'No' });
        }
        return text;
    };
    return (
        <ValueChangeCell beforeValue={beforeValue} afterValue={afterValue} valueText={valueText} />
    );
}

function InheritedCell({ row }: RowCellProps) {
    const { formatMessage } = useIntl();
    if (row.original.inheritedCount <= 0) return <span>—</span>;
    return (
        <span>
            {formatMessage(
                {
                    id: 'findReplace.table.inheritedCount',
                    defaultMessage: '{count, plural, one {# object} other {# objects}}',
                },
                { count: row.original.inheritedCount },
            )}
        </span>
    );
}

type FindReplaceRowsTableProps = {
    id: string;
    hierarchyId: string;
    rows: FindReplaceRowDto[];
    count: number;
    loading: boolean;
    page: number;
    pageSize: number;
    onPageChange: (page: number, pageSize: number) => void;
    /** The actions of the run (`run.input.actions`), indexed by the rows' actionIndex. */
    actions: BulkChangeAction[];
    labeler: ContentTypeLabeler;
    /** Columns rendered before the node code (e.g. the inclusion checkbox). */
    leadingColumns?: RowColumn[];
    /** Columns rendered after the change (e.g. the skip reason). */
    trailingColumns?: RowColumn[];
    /** Hides the "Inherited by" column (the result shows the reason instead). */
    hideInherited?: boolean;
    emptyMessage: string;
};

export function FindReplaceRowsTable({
    id,
    hierarchyId,
    rows,
    count,
    loading,
    page,
    pageSize,
    onPageChange,
    actions,
    labeler,
    leadingColumns = NO_COLUMNS,
    trailingColumns = NO_COLUMNS,
    hideInherited = false,
    emptyMessage,
}: FindReplaceRowsTableProps) {
    const { formatMessage } = useIntl();
    const { searchDataPagination } = useContext(SettingsContext);
    const deletes = isDeleteRun({ actions });
    const [cascadeRow, setCascadeRow] = useState<FindReplaceRowDto | null>(null);

    const regexByAction = useMemo(
        () =>
            new Map(
                actions.flatMap((action, index) =>
                    action.kind === 'replaceText'
                        ? [[index, findRegexOrNull(action)] as const]
                        : [],
                ),
            ),
        [actions],
    );

    // The link ids of the page, resolved to their names with one query.
    const linkRows = useMemo(
        () =>
            rows.filter(row =>
                LINK_TYPES.includes(labeler.contentType(row.ctg, row.ct)?.type ?? ''),
            ),
        [rows, labeler],
    );
    const linkHierarchies = useMemo(
        () =>
            linkRows.flatMap(row => {
                const hierarchy = labeler.contentType(row.ctg, row.ct)?.hierarchy;
                return hierarchy ? [hierarchy] : [];
            }),
        [linkRows, labeler],
    );
    const linkIds = useMemo(
        () =>
            linkRows.flatMap(row =>
                [...(row.beforeValue ?? []), ...(row.afterValue ?? [])].map(linkIdOf),
            ),
        [linkRows],
    );
    const { byId } = useNodesById(linkHierarchies, linkIds);

    const contextValue = useMemo(
        () => ({
            hierarchyId,
            actions,
            regexByAction,
            labeler,
            nodeLabel: (nodeId: string) => {
                const node = byId.get(nodeId);
                return node ? nodeOptionLabel(node) : nodeId;
            },
            showCascade: setCascadeRow,
        }),
        [hierarchyId, actions, regexByAction, labeler, byId],
    );

    const columns = useMemo<RowColumn[]>(
        () =>
            deletes
                ? [
                      ...leadingColumns,
                      {
                          id: 'nodeCode',
                          accessorKey: 'nodeCode',
                          header: formatMessage({
                              id: 'findReplace.table.node',
                              defaultMessage: 'Node',
                          }),
                          cell: DeleteNodeCodeCell,
                      },
                      {
                          id: 'action',
                          header: formatMessage({
                              id: 'bulkChange.table.action',
                              defaultMessage: 'Action',
                          }),
                          cell: ActionCell,
                      },
                      {
                          id: 'descendants',
                          header: formatMessage({
                              id: 'bulkChange.table.descendants',
                              defaultMessage: 'Descendants also deleted',
                          }),
                          maxWidth: false,
                          cell: DescendantsCell,
                      },
                      ...trailingColumns,
                  ]
                : [
                      ...leadingColumns,
                      {
                          id: 'nodeCode',
                          accessorKey: 'nodeCode',
                          header: formatMessage({
                              id: 'findReplace.table.node',
                              defaultMessage: 'Node',
                          }),
                          cell: NodeCodeCell,
                      },
                      {
                          id: 'action',
                          header: formatMessage({
                              id: 'bulkChange.table.action',
                              defaultMessage: 'Action',
                          }),
                          cell: ActionCell,
                      },
                      {
                          id: 'contentType',
                          header: formatMessage({
                              id: 'findReplace.table.contentType',
                              defaultMessage: 'Field',
                          }),
                          cell: ContentTypeCell,
                      },
                      {
                          id: 'lang',
                          header: formatMessage({
                              id: 'bulkChange.table.lang',
                              defaultMessage: 'Lang',
                          }),
                          size: 70,
                          cell: LangCell,
                      },
                      {
                          id: 'diff',
                          header: formatMessage({
                              id: 'findReplace.table.diff',
                              defaultMessage: 'Change',
                          }),
                          maxWidth: false,
                          cell: ValueDiffCell,
                      },
                      ...(hideInherited
                          ? []
                          : [
                                {
                                    id: 'inheritedCount',
                                    header: formatMessage({
                                        id: 'findReplace.table.inherited',
                                        defaultMessage: 'Inherited by',
                                    }),
                                    cell: InheritedCell,
                                },
                            ]),
                      ...trailingColumns,
                  ],
        [deletes, leadingColumns, trailingColumns, hideInherited, formatMessage],
    );

    // The row count changes with the filters: clamp so the table never asks for a page past the end.
    const lastPage = Math.max(0, Math.ceil(count / pageSize) - 1);
    const safePage = Math.min(page, lastPage);

    if (!loading && rows.length === 0) {
        return (
            <Box p={3}>
                <Typography color="textSecondary">{emptyMessage}</Typography>
            </Box>
        );
    }

    return (
        <RowsTableContext.Provider value={contextValue}>
            <BaseTable
                id={id}
                data={rows}
                columns={columns}
                count={count}
                isLoading={loading}
                selectedRows={[]}
                initialFilters={[]}
                disabledFilters={[]}
                manualPagination
                pageIndex={safePage}
                hasPreviousPage={safePage > 0}
                hasNextPage={(safePage + 1) * pageSize < count}
                getTablePaginationRowsLabel={() =>
                    getTablePaginationRowsLabel(pageSize, safePage, count)
                }
                handleStateChange={state => {
                    if (state.pagination) {
                        onPageChange(state.pagination.pageIndex, state.pagination.pageSize);
                    }
                }}
                settings={{ pagination: searchDataPagination || undefined }}
                excludeDndProvider
            />
            {cascadeRow && (
                <CascadeDeletePreviewDialog
                    hierarchyId={hierarchyId}
                    nodeId={cascadeRow.nodeId}
                    nodeCode={cascadeRow.nodeCode}
                    onClose={() => setCascadeRow(null)}
                />
            )}
        </RowsTableContext.Provider>
    );
}
